import { useEffect, useState, useSyncExternalStore } from 'react'
import { RefreshCw, X } from 'lucide-react'
import { applyUpdate, isUpdateReady, subscribeToUpdates } from '@/lib/serviceWorker'
import { useT } from '@/i18n/I18nProvider'
import { cn } from '@/lib/utils'
import { Button } from './ui'

/**
 * "A new version is out — reload when you are ready."
 *
 * The gap this closes: `registerType: 'autoUpdate'` swaps the service worker
 * silently while the open tab keeps running the old JavaScript. A tab left open
 * across a deploy shows stale data and stale code, and until now the only thing
 * that noticed was a lazy chunk 404ing — after the fact, on the error boundary,
 * on a screen that was never broken.
 *
 * ## Why this cannot reload by itself
 *
 * Because it would eat somebody's work. A report is filled in next to a machine
 * with one thumb; an evaluation is up to 73 taps collected while walking. A
 * reload that arrives uninvited in the middle of either is a bug wearing a
 * feature's clothes, and the person it happens to has no way to know what hit
 * them.
 *
 * So the reload is a tap, never a timer, and the sentence next to the button
 * says what the tap costs. Three things back that up:
 *
 * 1. The banner holds while a field has the caret — see `useTypingLull`. Not
 *    because appearing is dangerous, but because a control that materialises
 *    under a thumb that is already moving gets pressed.
 * 2. It is a strip at the bottom edge, above the tab bar, not a
 *    modal. A modal over a half-filled form is the same loss with more steps.
 * 3. `applyUpdate` reloads through `location.reload()`, which fires
 *    `beforeunload` — so the unsaved-scores guard in pages/FiveS.tsx still gets
 *    to put the browser's own confirmation in front of it.
 *
 * ## What is genuinely not protected
 *
 * There is no application-wide record of which forms are dirty, and this
 * component cannot see React state it does not own. A half-typed submission on
 * `/prijave/nova` is lost if somebody taps through the warning. What *is* safe
 * is anything already handed to the offline queue: that lives in IndexedDB and
 * survives a reload, which is why the sentence says so rather than making the
 * blanket "nothing has been lost" promise that `error.staleBuild.body` makes
 * about the after-the-fact case.
 */
export function UpdatePrompt() {
  const ready = useSyncExternalStore(subscribeToUpdates, isUpdateReady, () => false)
  const [dismissed, setDismissed] = useState(false)
  const typing = useTypingLull()
  const t = useT()

  if (!ready || dismissed || typing) return null

  return (
    <div
      // `status`, not `alert`. An alert interrupts a screen reader mid-sentence,
      // and nothing here is urgent — the app keeps working on the old build.
      role="status"
      aria-live="polite"
      className={cn(
        'fixed z-40 flex items-start gap-3 rounded-2xl p-3',
        /*
         * Just above the tab bar, the full width of it. It used to stop at
         * `right-20` to clear the floating report button on the same line;
         * the actions moved into the bar itself, so nothing is there to
         * clear. Below the tab bar's z-30 would put this under it.
         */
        'right-3 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] left-3',
        'lg:right-auto lg:bottom-12 lg:left-4 lg:w-96',
        // The tab bar's glass, for the same reason it has it: this floats over
        // content, and an opaque panel would read as part of the page.
        'border border-black/5 shadow-lg dark:border-white/10',
        'bg-card/85 backdrop-blur-2xl backdrop-saturate-150 supports-[backdrop-filter]:bg-card/70',
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{t('error.staleBuild.title')}</p>
        <p className="muted mt-0.5 text-xs">{t('update.consequence')}</p>

        <Button className="mt-2" onClick={() => void applyUpdate()}>
          <RefreshCw className="size-4" aria-hidden />
          {t('error.staleBuild.reload')}
        </Button>
      </div>

      {/*
        Dismissing hides it for this page load only. There is nothing to
        remember: the signal is raised once per tab per deploy, so the next one
        asks again, and a tab that is never reloaded is exactly the tab this is
        about.
      */}
      <Button variant="ghost" aria-label={t('action.close')} onClick={() => setDismissed(true)}>
        <X className="size-4" aria-hidden />
      </Button>
    </div>
  )
}

/**
 * True while the caret is in something somebody is typing into.
 *
 * `focusin`/`focusout` rather than `focus`/`blur`: only the first pair bubbles,
 * and this has to hear about a field anywhere below the shell without knowing
 * anything about it.
 *
 * It reads `event.target` rather than `document.activeElement`, which is not a
 * detail. `focusout` fires *before* the next element is focused, so
 * `activeElement` is `<body>` at that instant — and clicking this component's
 * own Reload button is a `focusin` like any other. Keyed on the target, the
 * button is not a text entry and the banner stays put; keyed on
 * `activeElement`, it would vanish out from under the tap that was meant to
 * dismiss it.
 *
 * Moving between two fields fires `focusout` then `focusin` in the same task,
 * so React batches the false and the true into one render and the banner does
 * not flicker on tab.
 */
function useTypingLull(): boolean {
  const [typing, setTyping] = useState(() => isTextEntry(document.activeElement))

  useEffect(() => {
    const entered = (event: FocusEvent) => setTyping(isTextEntry(event.target))
    const left = () => setTyping(false)

    document.addEventListener('focusin', entered)
    document.addEventListener('focusout', left)

    return () => {
      document.removeEventListener('focusin', entered)
      document.removeEventListener('focusout', left)
    }
  }, [])

  return typing
}

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false

  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  )
}
